import React from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { ClompApi } from '../api/ClompApi';
import { SESSION_ENDED_EVENT, endSession } from '../api/session';
import './SiteExtras.css';

const api = new ClompApi();

// Pages these don't appear on: the bingo event is a project of its own
const LEFT_OUT = ['/bingo'];

// How long typing has to pause before a new username is checked
const USERNAME_CHECK_DELAY = 350;
// What a username may be (the backend has the final say: see userRules.js)
const USERNAME_MIN_LENGTH = 3;
const USERNAME_MAX_LENGTH = 25;
const FEEDBACK_MAX_LENGTH = 2000;

const VISITOR_KEY = 'visitorID';
// used when the browser won't keep one, so a visit is still counted, as a new visitor each time the site is opened
let fallbackVisitor = null;

// This browser's own random id, which tells visitors apart in the site's analytics and names nobody
function visitorID() {
    let make = () => (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID()
        : Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
    try {
        let id = localStorage.getItem(VISITOR_KEY);
        if (!id) {
            id = make();
            localStorage.setItem(VISITOR_KEY, id);
        }
        return id;
    } catch (e) {
        fallbackVisitor = fallbackVisitor || make();
        return fallbackVisitor;
    }
}

// Counts each page opened, for the admin panel's analytics
function VisitCounter() {
    let { pathname } = useLocation();
    React.useEffect(() => {
        api.feedback.countVisit(pathname, visitorID()).catch(() => { /* a visit that isn't counted is no loss */ });
    }, [pathname]);
    return null;
}

// The button in the bottom left corner of every page for sending a bug report or a suggestion, and the form it opens.
// Only logged-in players can send one; anyone else is asked to log in.
function FeedbackButton() {
    let { pathname } = useLocation();
    let [open, setOpen] = React.useState(false);
    let [kind, setKind] = React.useState('bug');
    let [message, setMessage] = React.useState('');
    let [sending, setSending] = React.useState(false);
    let [error, setError] = React.useState(null);
    let [sent, setSent] = React.useState(false);
    let loggedIn = localStorage.getItem('token') !== null;

    let close = React.useCallback(() => {
        if (!sending) {
            setOpen(false);
            setError(null);
            if (sent) {
                setSent(false);
                setMessage('');
            }
        }
    }, [sending, sent]);

    React.useEffect(() => {
        if (!open) {
            return undefined;
        }
        let onKey = (e) => e.key === 'Escape' && close();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, close]);

    let send = (e) => {
        e.preventDefault();
        if (message.trim().length < 5) {
            setError('Tell us a little more (at least 5 characters)');
            return;
        }
        setSending(true);
        setError(null);
        api.feedback.send(kind, message, pathname).then(() => {
            setSending(false);
            setSent(true);
        }).catch((err) => {
            let data = err.response && err.response.data;
            setSending(false);
            setError((data && data.message) || "Couldn't send that. Please try again.");
        });
    };

    return (
        <>
            <button type="button" className="sx-feedback-btn" onClick={() => setOpen(true)} aria-haspopup="dialog" title="Report a bug or make a suggestion">
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                    <path fill="currentColor" d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm-7 12h-2v-2h2v2zm0-4h-2V6h2v4z" />
                </svg>
                <span>Bug or idea?</span>
            </button>
            {open ?
                <div className="sx-modal" role="dialog" aria-modal="true" aria-labelledby="sx-feedback-title" onMouseDown={(e) => e.target === e.currentTarget && close()}>
                    <div className="sx-modal-body">
                        <h2 id="sx-feedback-title">Bugs and suggestions</h2>
                        {!loggedIn ?
                            <>
                                <p className="sx-note">Log in to send us a bug report or a suggestion.</p>
                                <div className="sx-actions">
                                    <button type="button" className="sx-btn sx-btn-ghost" onClick={close}>Close</button>
                                    <Link className="sx-btn sx-btn-primary" to="/login" onClick={close}>Log in</Link>
                                </div>
                            </>
                            : sent ?
                            <>
                                <p className="sx-done" role="status">Thanks! Your {kind === 'bug' ? 'bug report' : 'suggestion'} has been sent to the team.</p>
                                <div className="sx-actions">
                                    <button type="button" className="sx-btn sx-btn-primary" onClick={close} autoFocus>Done</button>
                                </div>
                            </>
                            :
                            <form onSubmit={send} noValidate autoComplete="off">
                                <div className="sx-kinds" role="radiogroup" aria-label="What are you sending?">
                                    {[['bug', 'Bug report'], ['suggestion', 'Suggestion']].map(([id, label]) => (
                                        <button key={id} type="button" role="radio" aria-checked={kind === id} className={kind === id ? 'is-on' : ''} onClick={() => setKind(id)}>{label}</button>
                                    ))}
                                </div>
                                <label className="sx-label" htmlFor="sx-feedback-message">
                                    {kind === 'bug' ? 'What went wrong? What were you doing when it happened?' : 'What would you like to see?'}
                                </label>
                                <textarea id="sx-feedback-message" rows="6" maxLength={FEEDBACK_MAX_LENGTH} value={message} autoFocus
                                    onChange={(e) => { setMessage(e.target.value); setError(null); }} />
                                <p className="sx-hint">{message.length} / {FEEDBACK_MAX_LENGTH}. The page you're on is sent with it.</p>
                                {error ? <p className="sx-error" role="alert">{error}</p> : null}
                                <div className="sx-actions">
                                    <button type="button" className="sx-btn sx-btn-ghost" disabled={sending} onClick={close}>Cancel</button>
                                    <button type="submit" className="sx-btn sx-btn-primary" disabled={sending}>{sending ? 'Sending...' : 'Send'}</button>
                                </div>
                            </form>
                        }
                    </div>
                </div>
                : null}
        </>
    );
}

// Accounts whose usernames are from before the rules (letters and numbers only, 3 to 25 long) must choose a new one
// before going on. This asks the backend once per login, and if so shows a window that can't be closed except by
// choosing a name that can be had, or logging out.
function RenameGate() {
    let history = useHistory();
    let { pathname } = useLocation();
    let [needed, setNeeded] = React.useState(null);
    let [name, setName] = React.useState('');
    let [check, setCheck] = React.useState(null);
    let [saving, setSaving] = React.useState(false);
    let [error, setError] = React.useState(null);
    let checked = React.useRef(null);
    let timer = React.useRef(null);
    let token = localStorage.getItem('token');

    React.useEffect(() => {
        if (token === null || checked.current === token) {
            return;
        }
        checked.current = token;
        api.auth.getSession().then((session) => {
            if (session.loggedIn && session.mustRename && localStorage.getItem('token') === token) {
                setNeeded(session.username);
            }
        }).catch(() => { /* asked again on the next login */ });
    }, [token, pathname]);

    React.useEffect(() => {
        let onEnded = () => setNeeded(null);
        window.addEventListener(SESSION_ENDED_EVENT, onEnded);
        return () => {
            window.removeEventListener(SESSION_ENDED_EVENT, onEnded);
            clearTimeout(timer.current);
        };
    }, []);

    if (needed === null || token === null) {
        return null;
    }

    let onChange = (e) => {
        let value = e.target.value;
        let wanted = value.trim();
        setName(value);
        setError(null);
        clearTimeout(timer.current);
        if (wanted === '') {
            setCheck(null);
            return;
        }
        setCheck({ name: wanted, status: 'checking' });
        timer.current = setTimeout(() => {
            api.profile.checkUsername(wanted).then((result) => {
                setCheck((now) => now && now.name === wanted ? { name: wanted, status: result.available ? 'available' : 'unavailable', message: result.message } : now);
            }).catch(() => setCheck((now) => now && now.name === wanted ? null : now));
        }, USERNAME_CHECK_DELAY);
    };

    let save = (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        api.profile.saveSettings({ username: name.trim() }).then(() => {
            setSaving(false);
            setNeeded(null);
            // a profile opened by the old name is no longer at that address
            if (pathname.startsWith('/profile/')) {
                history.replace('/profile');
            }
        }).catch((err) => {
            let data = err.response && err.response.data;
            setSaving(false);
            setError((data && data.message) || "Couldn't save your new username. Please try again.");
        });
    };

    let logOut = () => {
        api.auth.logout(token).catch(() => {});
        endSession(false);
        history.push('/');
    };

    let available = check && check.status === 'available';
    let unavailable = check && check.status === 'unavailable';
    return (
        <div className="sx-modal" role="dialog" aria-modal="true" aria-labelledby="sx-rename-title">
            <div className="sx-modal-body">
                <h2 id="sx-rename-title">Choose a new username</h2>
                <p className="sx-note">
                    Usernames can now only use letters (a-z) and numbers (0-9), and be {USERNAME_MIN_LENGTH} to {USERNAME_MAX_LENGTH} characters long.
                    Your username, <strong>{needed}</strong>, doesn't fit, so please pick a new one. Your profile, scores and cards stay with you.
                </p>
                <form onSubmit={save} noValidate autoComplete="off">
                    <label className="sx-label" htmlFor="sx-rename-input">New username</label>
                    <div className="sx-input-wrap">
                        <input id="sx-rename-input" type="text" value={name} maxLength={USERNAME_MAX_LENGTH} autoFocus onChange={onChange}
                            autoComplete="off" data-lpignore="true" data-1p-ignore="true" data-bwignore="true" aria-invalid={Boolean(unavailable || error)} />
                        {available || unavailable ?
                            <span className={available ? 'sx-mark sx-mark-ok' : 'sx-mark sx-mark-bad'} role="img" aria-label={available ? 'Username available' : 'Username unavailable'}>
                                {available ? '✓' : '✕'}
                            </span>
                            : null}
                    </div>
                    {error ? <p className="sx-error" role="alert">{error}</p>
                        : unavailable ? <p className="sx-error" role="status">{check.message}</p>
                        : <p className="sx-hint">Capitals are kept, but don't make a name different from one that's taken.</p>}
                    <div className="sx-actions">
                        <button type="button" className="sx-btn sx-btn-ghost" disabled={saving} onClick={logOut}>Log out</button>
                        <button type="submit" className="sx-btn sx-btn-primary" disabled={saving || !available}>{saving ? 'Saving...' : 'Save username'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// What every page of the site has besides its own content: the bug and suggestion button, the rename window for
// accounts that need one, and the visit counter. Goes inside the Router.
export function SiteExtras() {
    let { pathname } = useLocation();
    let leftOut = LEFT_OUT.some((page) => pathname === page || pathname.startsWith(page + '/'));
    return (
        <>
            <VisitCounter />
            {leftOut ? null : <FeedbackButton />}
            <RenameGate />
        </>
    );
}
